import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type { PrintFormatLabel } from '../data/printFormat';

/**
 * Per-language strings for a print format.
 *
 * The write surface is a bulk upsert rather than row CRUD: the editor saves a
 * whole language at once, and `(format, language, key)` is uniquely indexed
 * server-side, so a plain insert would collide on the second save of the same
 * screen.
 *
 * Sending a blank `labelText` DELETES the row — clearing a box means "fall back
 * to the renderer's built-in string", which a stored empty value would defeat.
 */
export function createPrintFormatLabelService(client: AxiosInstance) {
  return {
    /** Labels for a format. Omit `languageCode` to get every language. */
    forFormat: async (printFormatId: string, languageCode?: string): Promise<PrintFormatLabel[]> => {
      const res = await client.post<ObjectResponse<PrintFormatLabel[]>>(
        '/PrintFormatLabel/for-format',
        {},
        { params: { printFormatId, languageCode } },
      );
      return unwrapItems(res.data);
    },

    /** Insert-or-update by (format, language, key). Blank text deletes. */
    upsert: async (items: PrintFormatLabel[]): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/PrintFormatLabel/upsert',
        { items },
      );
      return unwrap(res.data) ?? false;
    },

    remove: async (label: PrintFormatLabel): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/PrintFormatLabel/Delete',
        label,
      );
      return unwrap(res.data) ?? false;
    },
  };
}

export type PrintFormatLabelService = ReturnType<typeof createPrintFormatLabelService>;
